import { useTranslation } from 'react-i18next';
import styles from '../../HomepageSections.module.scss';
import { SectionHeading } from '../shared/SectionHeading';

const formatDestinations = [
  { format: 'XRechnung', country: 'DE' },
  { format: 'Factur-X', country: 'FR' },
  { format: 'CFDI 4.0', country: 'MX' },
  { format: 'ZATCA', country: 'SA' },
  { format: 'Peppol', country: 'AU' }
];

const formatRayPaths = [
  'M 0 85 C 60 85 110 20 200 20',
  'M 0 85 C 60 85 110 52 200 52',
  'M 0 85 C 60 85 110 85 200 85',
  'M 0 85 C 60 85 110 118 200 118',
  'M 0 85 C 60 85 110 150 200 150'
];

const weekDays = [
  { key: 'monday', x: 18 },
  { key: 'tuesday', x: 86 },
  { key: 'wednesday', x: 152 },
  { key: 'thursday', x: 218 },
  { key: 'friday', x: 284 }
];

export const PlatformSection = () => {
  const { t } = useTranslation('landing');

  return (
    <section className={styles.platformSection} id='features'>
      <div className={styles.wideShell}>
        <div className={styles.featureHeader}>
          <SectionHeading eyebrow={t('homepage.platform.eyebrow')} title={t('homepage.platform.title')} />
          <p>{t('homepage.platform.description')}</p>
        </div>
        <div className={styles.featureGrid}>
          <article className={`${styles.featureCard} ${styles.workflowCard}`}>
            <div>
              <h3>{t('homepage.platform.workflow.title')}</h3>
              <p>{t('homepage.platform.workflow.description')}</p>
            </div>
            <svg className={styles.weekline} viewBox='0 0 320 110' aria-hidden='true'>
              {weekDays.map((day) => (
                <text x={day.x} y='18' key={day.key}>
                  {t(`homepage.platform.workflow.days.${day.key}`)}
                </text>
              ))}
              <path className={styles.weekGrid} d='M12 30v66M78 30v66M144 30v66M210 30v66M276 30v66' />
              <path className={styles.weekTrack} d='M12 63h296' />
              {[0, 1.1, 2.2, 3.1].map((delay) => (
                <g
                  className={styles.weekTrail}
                  style={{ offsetPath: "path('M 12 63 L 308 63')", animationDelay: `${delay}s` }}
                  key={delay}
                >
                  <circle r='3' />
                </g>
              ))}
              <g className={styles.digestPop}>
                <rect x='252' y='32' width='62' height='24' rx='12' />
                <text x='283' y='47.5' textAnchor='middle'>
                  {t('homepage.platform.workflow.digest')}
                </text>
              </g>
              <text className={styles.weekSummary} x='160' y='96' textAnchor='middle'>
                {t('homepage.platform.workflow.summary')}
              </text>
            </svg>
          </article>

          <article className={styles.featureCard}>
            <div className={styles.currencyVisual} aria-hidden='true'>
              <div className={styles.currencyPerspective}>
                <div className={styles.currencyCoin}>
                  <span>€</span>
                  <b>$</b>
                </div>
              </div>
              <div className={styles.currencyExchange}>
                <span>EUR 48,200.00</span>
                <svg viewBox='0 0 14 12'>
                  <path d='M2 3h8M8 1l2.5 2L8 5M12 9H4m2-2L3.5 9 6 11' />
                </svg>
                <strong>USD 52,341.20</strong>
              </div>
            </div>
            <div>
              <h3>{t('homepage.platform.currency.title')}</h3>
              <p>{t('homepage.platform.currency.description')}</p>
            </div>
          </article>

          <article className={styles.featureCard}>
            <svg className={styles.languageDiagram} viewBox='0 0 140 74' aria-hidden='true'>
              <rect x='6' y='8' width='50' height='58' rx='6' />
              <text x='31' y='30' textAnchor='middle'>
                A
              </text>
              <path className={styles.languageSourceLines} d='M16 42h30M16 50h30M16 58h20' />
              <path className={styles.animatedDash} d='M62 37h14' />
              <path className={styles.languageArrow} d='m73 33 5 4-5 4' />
              <rect className={styles.languageTargetPage} x='84' y='8' width='50' height='58' rx='6' />
              <text className={styles.languageTargetGlyph} x='109' y='30' textAnchor='middle'>
                文
              </text>
              <path className={styles.languageTargetLine} d='M94 42h30' />
              <path className={styles.languageTargetLine} d='M94 50h30' style={{ animationDelay: '0.35s' }} />
              <path className={styles.languageTargetLine} d='M94 58h20' style={{ animationDelay: '0.7s' }} />
            </svg>
            <div>
              <h3>{t('homepage.platform.language.title')}</h3>
              <p>{t('homepage.platform.language.description')}</p>
            </div>
          </article>

          <article className={styles.featureCard}>
            <svg className={styles.aiDiagram} viewBox='0 0 140 74' aria-hidden='true'>
              <path className={styles.aiBaseline} d='M8 52c16 0 14-28 30-22 14 5 12 18 26 14' />
              <path className={styles.aiRewriteLine} d='M64 44c20-6 32-6 68-6' />
              <path className={styles.aiSparkle} d='m64 24 2.2 6.2 6.3 2.3-6.3 2.3L64 41l-2.2-6.2-6.3-2.3 6.3-2.3z' />
              <path
                className={styles.aiSparkle}
                d='m112 20 1.4 4 4.1 1.5-4.1 1.5-1.4 4-1.4-4-4.1-1.5 4.1-1.5z'
                style={{ animationDelay: '1.1s' }}
              />
            </svg>
            <div>
              <h3>{t('homepage.platform.ai.title')}</h3>
              <p>{t('homepage.platform.ai.description')}</p>
            </div>
          </article>

          <article className={styles.featureCard}>
            <div className={styles.errorVisual} aria-hidden='true'>
              <div className={styles.errorRow}>
                <span />
                <small>OK</small>
              </div>
              <div className={styles.errorRow}>
                <span />
                <small>OK</small>
              </div>
              <div className={styles.errorRepair}>
                <span />
                <i>VAT ✕</i>
                <b>{t('homepage.platform.error.fixed')} ✓</b>
              </div>
              <div className={styles.errorRow}>
                <span />
                <small>OK</small>
              </div>
            </div>
            <div>
              <h3>{t('homepage.platform.error.title')}</h3>
              <p>{t('homepage.platform.error.description')}</p>
            </div>
          </article>

          <article className={`${styles.featureCard} ${styles.formatCard}`}>
            <div>
              <h3>{t('homepage.platform.format.title')}</h3>
              <p>{t('homepage.platform.format.description')}</p>
            </div>
            <div className={styles.formatFan} aria-hidden='true'>
              <div className={styles.canonicalDocument}>
                <div className={styles.canonicalHeader}>
                  <span>{t('homepage.platform.format.canonical')}</span>
                  <b>e</b>
                </div>
                <i className={styles.canonicalPrimaryLine} />
                <i />
                <i />
                <i />
                <div className={styles.canonicalTotal}>
                  <i />
                  <strong>€48,200</strong>
                </div>
              </div>
              <svg className={styles.formatRays} viewBox='0 0 200 170' preserveAspectRatio='none'>
                {formatRayPaths.map((path) => (
                  <path className={styles.formatRay} d={path} key={path} />
                ))}
                {formatRayPaths.map((path, index) => (
                  <g
                    className={styles.formatTravelDot}
                    style={{ offsetPath: `path('${path}')`, animationDelay: `${index * 0.8}s` }}
                    key={`travel-${path}`}
                  >
                    <circle r='3.5' />
                  </g>
                ))}
              </svg>
              <div className={styles.formatChips}>
                {formatDestinations.map(({ format, country }, index) => (
                  <span style={{ animationDelay: `${0.55 + index * 0.8}s` }} key={format}>
                    {format} <b>{country}</b>
                  </span>
                ))}
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
};
